import createCachedSelector from "re-reselect";
import type {ProductData} from "../../models/ProductData";
import type {PriceInfo} from "../../api/pna/PriceInfo";
import type {AvailabilityInfo} from "../../api/pna/AvailabilityInfo";
import type {ProductsStore} from "./types";
import type {LoadableEntry} from "../loadableStore/types";
import {createSelector} from "reselect";
import type {RootState} from "../store";
import {canSeeCountryOrigin} from "../../utils/siteModes";
import {makeDescriptorFromFullState} from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import type {WishlistEntryModel} from "~/Wishlist/model";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

type Product13DigitParams = { productCode: string; priceReference: string };
type Product15DigitParams = Product13DigitParams & { packCode?: string };

function fromRootState4Hook<P, R>(fun: (state: ProductsStore, params: P) => R) {
  return (p: P) => (s: RootState) => fun(s.products, p);
}

const selectDeepBasicProductData = (state: ProductsStore, { productCode }: Pick<Product13DigitParams, "productCode">) =>
  state.basic[productCode];
const selectBasicProductData = fromRootState4Hook(selectDeepBasicProductData);

const selectDeepPriceData = (state: ProductsStore, params: Product13DigitParams) => {
  return state.price[getCacheKey({ productCode: params.productCode, priceReference: params.priceReference })];
};
export const selectPriceData = fromRootState4Hook(selectDeepPriceData);

const selectDeepAvailabilityData = (state: ProductsStore, params: Product13DigitParams) => {
  return state.availability[getCacheKey({ productCode: params.productCode })];
};
export const selectAvailabilityData = fromRootState4Hook(selectDeepAvailabilityData);

export const selectDeepProductDescriptor = createCachedSelector<
  ProductsStore,
  Product13DigitParams,
  ProductData,
  LoadableEntry<PriceInfo[]> | undefined,
  LoadableEntry<AvailabilityInfo> | undefined,
  string,
  ProductDescriptor
>(
  selectDeepBasicProductData,
  selectDeepPriceData,
  selectDeepAvailabilityData,
  (state, { priceReference }) => priceReference,
  makeDescriptorFromFullState,
)((state, { productCode, priceReference }) => productCode + "-" + priceReference);

function fromRootState<P, R>(fun: (state: ProductsStore, params: P) => R): (state: RootState, params: P) => R {
  return (s, p) => fun(s.products, p);
}
export const selectProductDescriptor = fromRootState(selectDeepProductDescriptor);

export const getProductDescriptorMap = createSelector(
  (state: RootState, { wishlistEntries }: { wishlistEntries: WishlistEntryModel[] }) => state,
  (state: RootState, { wishlistEntries }: { wishlistEntries: WishlistEntryModel[] }) => wishlistEntries,
  (state, wishlistEntries) => {
    return (
      Object.values(wishlistEntries)
        // Using wishlistEntries in order to have access to the priceReference
        .map((basicData) =>
          selectProductDescriptor(state, {
            productCode: basicData.productCode,
            priceReference: basicData.priceReference,
          }),
        )
        .reduce((map, descriptor) => {
          map[descriptor.base.code] = descriptor;
          return map;
        }, {})
    );
  },
);

export const selectProductData =
  (productCode: string) =>
  (state: RootState): ProductData =>
    state.products.basic[productCode];

export const selectCountryOfOriginPerCode = createSelector(
  (prices: PriceInfo[]) => prices,
  (prices) => {
    if (!prices.length) {
      return undefined;
    }
    if (!canSeeCountryOrigin) {
      return undefined;
    }

    return prices.reduce(
      (map, priceInfo) => {
        map[priceInfo.packCode || ""] = priceInfo.originCountry || "";
        return map;
      },
      {} as { [packCode: string]: string },
    );
  },
);

export const getCacheKey = ({ productCode, priceReference = "" }: { productCode: string; priceReference?: string }) => {
  return productCode + "-" + priceReference;
};
